import * as Blockly from 'blockly/core'

import { EditablePreviewWorld, ObjectRuntimeState, Quat } from '../../types'
import {
  quatFromAxisAngle,
  quatFromRobloxOrientation,
  quatMultiply,
} from '../quat'
import {
  AbortError,
  delay,
  ExecutionContext,
  isAbortError,
  LUA_WAIT_SECONDS,
  throwIfAborted,
  YIELD_EVERY_STATEMENTS,
} from './executionControl'
import { ExpressionEvaluator } from './expressionEvaluator'
import {
  degToRad,
  HIDE_TRANSPARENCY,
  PreviewValue,
  SEE_THROUGH_TRANSPARENCY,
  SHOW_TRANSPARENCY,
  toNumber,
} from './values'

export class Interpreter {
  /** 直近の yield からの実行文数。wait なし長スタックの協調 yield に使う。 */
  private statementsSinceYield = 0
  /**
   * スクリプトローカル変数（Lua: `local <name> = 0`）。変数名→数値または文字列。
   * setVariableTo は値の型を保持（文字列も可）、changeVariableBy は数値加算。
   * 未設定は 0。オブジェクト（=スクリプト）ごとに 1 Interpreter なので、この Map が
   * そのスクリプトの変数スコープになる。
   */
  private readonly variables = new Map<string, PreviewValue>()
  /** プレイ開始時刻（performance.now ミリ秒）。sensing_timer の経過秒に使う。 */
  private playStartMs = 0
  /** control_clone で生成したクローン数。クローンの一意 ID/名前生成に使う。 */
  private cloneCounter = 0
  /**
   * 値ブロック評価器。variables は Map 参照を共有し、再代入される playStartMs と
   * 文ハンドラ側にも残る selfState / currentOrientation はアクセサで注入する。
   */
  private readonly evaluator: ExpressionEvaluator

  constructor(
    private readonly world: EditablePreviewWorld,
    private readonly self: string,
    private readonly options?: {
      /**
       * looks_showMessage（Lua: print）の出力先。Roblox Studio の Output
       * ウィンドウ相当の UI へ流すために Runtime / PreviewPanel が渡す。
       * 未指定時はコンソールのみ（従来挙動）。
       */
      onOutput?: (text: string) => void
      /**
       * sound_play（Lua: Sound:Play()）の再生通知。実際の音声再生は UI 層
       * （PreviewPanel）が objectId から音源を引いて行う。未指定時は無音。
       */
      onPlaySound?: (objectId: string) => void
    },
  ) {
    this.evaluator = new ExpressionEvaluator(
      this.variables,
      () => this.selfState(),
      (state) => this.currentOrientation(state),
      () => this.playStartMs,
    )
  }

  /**
   * 1 回のプレイ開始時に Runtime が呼ぶ。Lua がプレイ毎に `local var = 0` で
   * 変数を初期化し直すのに合わせて変数をクリアし、タイマー起点を記録する。
   */
  beginRun(startMs: number): void {
    this.variables.clear()
    this.playStartMs = startMs
  }

  /**
   * 文スタックの先頭ブロックから順に実行する。
   * 各文の実行前に self の存在確認はしない（destroy 後は world 側が no-op）。
   */
  async run(
    firstBlock: Blockly.Block | null,
    context: ExecutionContext,
  ): Promise<void> {
    try {
      await this.runStack(firstBlock, context)
    } catch (error) {
      if (isAbortError(error)) {
        return
      }
      throw error
    }
  }

  private async runStack(
    firstBlock: Blockly.Block | null,
    context: ExecutionContext,
  ): Promise<void> {
    let block = firstBlock
    while (block) {
      throwIfAborted(context.signal)
      if (block.isEnabled()) {
        await this.runStatement(block, context)
        await this.maybeYield(context)
      }
      block = block.getNextBlock()
    }
  }

  /**
   * wait を含まない長いスタックでも一定文数ごとに制御を譲り、
   * 他ハンドラの進行と stop() への応答性を確保する（協調的インターリーブ）。
   */
  private async maybeYield(context: ExecutionContext): Promise<void> {
    this.statementsSinceYield += 1
    if (this.statementsSinceYield >= YIELD_EVERY_STATEMENTS) {
      await this.yieldLoop(context)
    }
  }

  /**
   * context.wait（RunSession の共有クロック）があればそれを使い、なければ
   * 単体 delay() にフォールバックする。
   */
  private wait(seconds: number, context: ExecutionContext): Promise<void> {
    return context.wait ? context.wait(seconds) : delay(seconds, context.signal)
  }

  private async runStatement(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    switch (block.type) {
      case 'movement_moveSteps':
        this.world.moveBy(this.self, {
          x: this.evaluator.readNumber(block, 'VAL'),
          y: 0,
          z: 0,
        })
        return
      case 'movement_setCanCollide':
        // Lua: script.Parent.CanCollide = true
        this.world.setCanCollide(this.self, true)
        return
      case 'movement_unsetCanCollide':
        // Lua: script.Parent.CanCollide = false
        this.world.setCanCollide(this.self, false)
        return
      case 'movement_setAnchored':
        // Lua: script.Parent.Anchored = true。現在位置で固定する。
        this.world.setAnchored(this.self, true)
        return
      case 'movement_unsetAnchored':
        // Lua: script.Parent.Anchored = false。重力で落ちる動的剛体にする。
        this.world.setAnchored(this.self, false)
        return
      case 'events_setClickable':
        // Lua: ClickDetector を追加（クリック可能化）
        this.world.setClickable(this.self, true)
        return
      case 'events_unsetClickable':
        // Lua: ClickDetector を削除（クリック不可化）
        this.world.setClickable(this.self, false)
        return
      case 'events_setWalkSpeedTo':
        // Lua: humanoid.WalkSpeed = N。アバターの歩行速度を更新する。
        // VAL は field_number なので parseFieldNumber で直接取り出す。
        this.world.setAvatarWalkSpeed(
          this.evaluator.parseFieldNumber(block, 'VAL'),
        )
        return
      case 'looks_seeThrough':
        // Lua は Transparency=0.5（半透明＝見える）。直前に hide で visible=false
        // になっていても再表示されるよう visible も合わせる。
        this.world.setVisible(this.self, true)
        this.world.setTransparency(this.self, SEE_THROUGH_TRANSPARENCY)
        return
      case 'looks_show':
        // Lua は Transparency=0。本ランタイムは visible も合わせて整合させる
        // （seeThrough → show で透明度が 0.5 のまま残る不整合を防ぐ）。
        this.world.setVisible(this.self, true)
        this.world.setTransparency(this.self, SHOW_TRANSPARENCY)
        return
      case 'looks_hide':
        // Lua は Transparency=1。visible も合わせる。
        this.world.setVisible(this.self, false)
        this.world.setTransparency(this.self, HIDE_TRANSPARENCY)
        return
      case 'control_destroy':
        this.world.destroy(this.self)
        // Lua の :Destroy() 同様、同一オブジェクト配下の実行を止める。
        // 別ハンドラも含めて Runtime 側で中断させ、自身のスタックも巻き戻す。
        context.destroySelf()
        throw new AbortError()
      case 'control_wait':
        // Roblox の wait(n) は指定秒数か Heartbeat 間隔（約 1/30 秒）の長い方だけ
        // 待つ。実機同様に下限（LUA_WAIT_SECONDS）を保証する。
        await this.wait(
          Math.max(this.evaluator.readNumber(block, 'VAL'), LUA_WAIT_SECONDS),
          context,
        )
        return
      case 'control_repeat':
        await this.runRepeat(block, context)
        return
      case 'control_forever':
        await this.runForever(block, context)
        return
      case 'control_if':
        await this.runIf(block, context)
        return
      case 'control_ifElse':
        await this.runIfElse(block, context)
        return
      case 'control_while':
        await this.runWhile(block, context)
        return
      case 'events_sendMessage':
        this.dispatchSendMessage(block, context)
        return
      // --- 移動: 位置（Lua: script.Parent.Position = / +=）---
      case 'movement_setXYZPositionTo':
        this.world.setPosition(this.self, {
          x: this.evaluator.readNumber(block, 'X'),
          y: this.evaluator.readNumber(block, 'Y'),
          z: this.evaluator.readNumber(block, 'Z'),
        })
        return
      case 'movement_setXTo':
      case 'movement_setYTo':
      case 'movement_setZTo':
        this.setPositionAxis(block)
        return
      case 'movement_changeXPositionBy':
        this.world.moveBy(this.self, {
          x: this.evaluator.readNumber(block, 'VAL'),
          y: 0,
          z: 0,
        })
        return
      case 'movement_changeYPositionBy':
        this.world.moveBy(this.self, {
          x: 0,
          y: this.evaluator.readNumber(block, 'VAL'),
          z: 0,
        })
        return
      case 'movement_changeZPositionBy':
        this.world.moveBy(this.self, {
          x: 0,
          y: 0,
          z: this.evaluator.readNumber(block, 'VAL'),
        })
        return
      // --- 移動: 向き（完全姿勢を setOrientation で反映）---
      case 'movement_setYOrientationTo':
        // Lua: Orientation = Vector3.new(0, n, 0)（度・絶対）。
        this.world.setOrientation(
          this.self,
          quatFromRobloxOrientation(
            0,
            this.evaluator.readNumber(block, 'VAL'),
            0,
          ),
        )
        return
      case 'movement_setXYZOrientationTo':
        // Lua: Orientation = Vector3.new(x, y, z)（度・絶対・YXZ）。
        this.world.setOrientation(
          this.self,
          quatFromRobloxOrientation(
            this.evaluator.readNumber(block, 'X'),
            this.evaluator.readNumber(block, 'Y'),
            this.evaluator.readNumber(block, 'Z'),
          ),
        )
        return
      case 'movement_turnRightBy':
      case 'movement_changeYOrientationBy':
        // Lua: CFrame *= CFrame.Angles(0, math.rad(n), 0)（相対・局所）。
        this.applyRelativeRotation(block, 'y', 1)
        return
      case 'movement_turnLeftBy':
        // Lua: CFrame *= CFrame.Angles(0, math.rad(-n), 0)。
        this.applyRelativeRotation(block, 'y', -1)
        return
      case 'movement_changeXOrientationBy':
        // Lua: CFrame *= CFrame.Angles(math.rad(n), 0, 0)。
        this.applyRelativeRotation(block, 'x', 1)
        return
      case 'movement_changeZOrientationBy':
        // Lua: CFrame *= CFrame.Angles(0, 0, math.rad(n))。
        this.applyRelativeRotation(block, 'z', 1)
        return
      // --- 見た目: サイズ（Lua: script.Parent.Size = / +=）---
      case 'looks_setSizeTo': {
        const v = this.evaluator.readNumber(block, 'VAL')
        this.world.setSize(this.self, { x: v, y: v, z: v })
        return
      }
      case 'looks_setXSizeTo':
      case 'looks_setYSizeTo':
      case 'looks_setZSizeTo':
        this.setSizeAxis(block)
        return
      case 'looks_setXYZSizeTo':
        this.world.setSize(this.self, {
          x: this.evaluator.readNumber(block, 'X'),
          y: this.evaluator.readNumber(block, 'Y'),
          z: this.evaluator.readNumber(block, 'Z'),
        })
        return
      case 'looks_changeSizeBy':
      case 'looks_changeXSizeBy':
      case 'looks_changeYSizeBy':
      case 'looks_changeZSizeBy':
        this.changeSize(block)
        return
      // --- 見た目: 色 / マテリアル / 透明度 ---
      case 'looks_setBrickColorTo': {
        const color = this.evaluator.evaluateColor(
          block.getInputTargetBlock('VAL'),
        )
        if (color !== null) {
          this.world.setColor(this.self, color)
        }
        return
      }
      case 'looks_setMaterialTo': {
        const material = this.evaluator.evaluateMaterial(
          block.getInputTargetBlock('VAL'),
        )
        if (material !== null) {
          this.world.setMaterial(this.self, material)
        }
        return
      }
      case 'looks_setTransparencyTo':
        // Lua: Transparency = v / 100（入力は 0..100）。World が 0..1 にクランプ。
        this.world.setTransparency(
          this.self,
          this.evaluator.readNumber(block, 'VAL') / 100,
        )
        return
      case 'looks_showMessage': {
        // Lua: print(v)。プレビューは Studio Output 相当として出力 UI へ流す
        // （onOutput 未配線の環境でも確認できるようコンソールにも残す）。
        const text = this.evaluator.evaluateString(
          block.getInputTargetBlock('VAL'),
        )
        console.info(`[preview] ${text}`)
        this.options?.onOutput?.(text)
        return
      }
      // --- 変数（Lua: <name> = v / <name> += v。スクリプトローカル）---
      case 'variable_setVariableTo':
        this.setVariable(block, false)
        return
      case 'variable_changeVariableBy':
        this.setVariable(block, true)
        return
      // --- 音（Lua: script.Parent.Sound）---
      case 'sound_play':
        // Lua: Sound:Play()。再生自体は UI 層（PreviewPanel）が担うため、
        // onPlaySound 経由で通知する。Sound を持たないオブジェクトは
        // 実機でもエラー（Sound が nil）になるので警告してスキップする。
        if (this.selfState()?.soundVolume === undefined) {
          console.warn(
            `[preview] Sound を持たないオブジェクトの sound_play をスキップ: ${this.self}`,
          )
          return
        }
        this.options?.onPlaySound?.(this.self)
        return
      case 'sound_setVolumeTo':
        // Lua: Sound.Volume = v / 100。World が 0〜10 にクランプする。
        this.world.setSoundVolume(
          this.self,
          this.evaluator.readNumber(block, 'VAL') / 100,
        )
        return
      case 'sound_changeVolumeBy': {
        // Lua: Sound.Volume += v / 100。
        const current = this.selfState()?.soundVolume
        if (current === undefined) {
          return
        }
        this.world.setSoundVolume(
          this.self,
          current + this.evaluator.readNumber(block, 'VAL') / 100,
        )
        return
      }
      // --- イベント: アバター属性 / 制御: クローン ---
      case 'events_setJumpPowerTo':
        // Lua: humanoid.JumpPower = v。物理がジャンプ初速として参照する。
        this.world.setAvatarJumpPower(this.evaluator.readNumber(block, 'VAL'))
        return
      case 'events_setHealthTo':
        // Lua: humanoid.Health = v。0 以下で物理側が spawn へ復帰させる。
        this.world.setAvatarHealth(this.evaluator.readNumber(block, 'VAL'))
        return
      case 'control_clone':
        this.cloneObject(block, context)
        return
      default:
        this.skipUnsupported(block)
        return
    }
  }

  /**
   * variable_setVariableTo / variable_changeVariableBy を反映する。
   * 変数名は field 'VAR' のテキスト。relative=true で現在値へ加算、false で代入。
   */
  private setVariable(block: Blockly.Block, relative: boolean): void {
    const name = block.getField('VAR')?.getText()
    if (typeof name !== 'string' || name.length === 0) {
      return
    }
    if (relative) {
      // changeVariableBy（Lua: name += v）。常に数値加算。
      const next =
        toNumber(this.variables.get(name)) +
        this.evaluator.readNumber(block, 'VAL')
      this.variables.set(name, next)
      return
    }
    // setVariableTo（Lua: name = v）。値の型（数値/文字列）を保持する。
    this.variables.set(
      name,
      this.evaluator.evaluateValue(block.getInputTargetBlock('VAL')),
    )
  }

  /**
   * control_clone を反映する。フィールド VAL のオブジェクト名で複製元を探し、位置・
   * 寸法・色・マテリアルを複製した可視オブジェクトを追加する（Lua: 同名 Part を
   * Clone して workspace へ）。Clone() は配下の Script ごと複製するため、
   * テンプレート名に学習者が付けたプログラムをクローンでも起動する。
   */
  private cloneObject(block: Blockly.Block, context: ExecutionContext): void {
    const name = block.getFieldValue('VAL')
    if (typeof name !== 'string' || name.length === 0) {
      return
    }
    // 同名オブジェクトが複数あるとき（例: RJF06 の静的な雛形と実行時生成の
    // 火の玉）は、複製を実行している自分自身（this.self）が同名なら自分の状態を
    // 優先する。縮小判定（collisionScale）など実行時インスタンス固有の属性を
    // 取りこぼさないため。
    const selfState = this.world.getState().get(this.self)
    let source: ObjectRuntimeState | undefined =
      selfState?.name === name ? selfState : undefined
    if (!source) {
      for (const state of this.world.getState().values()) {
        if (state.name === name) {
          source = state
          break
        }
      }
    }
    if (!source) {
      console.warn(`[preview] control_clone: 複製元が見つかりません: ${name}`)
      return
    }
    // 1 プレイの生成上限に達したら addObject せず静かに打ち切る（forever/repeat 内の
    // クローン生成で World が無限に膨らむのを防ぐ安全網）。
    if (context.tryConsumeCloneBudget?.() === false) {
      return
    }
    // 既存 ID と衝突しない一意 ID を採る（addObject は重複 ID で throw する）。
    let id: string
    do {
      this.cloneCounter += 1
      id = `${this.self}_clone_${this.cloneCounter}`
    } while (this.world.exists(id))
    // 位置・寸法・色・マテリアル・形状に加え、姿勢・透明度・衝突/固定も複製元から
    // 引き継ぐ。複製元のスクリプトは複製しない（割り切り）。
    this.world.addObject({
      id,
      name: `${name}#clone${this.cloneCounter}`,
      position: { ...source.position },
      size: { ...source.size },
      color: source.color,
      material: source.material,
      physicalProperties: source.physicalProperties,
      rotationY: source.rotationY,
      orientation: { ...source.orientation },
      transparency: source.transparency,
      canCollide: source.canCollide,
      canTouch: source.canTouch,
      isStatic: source.anchored,
      shape: source.shape,
      fire: source.fire,
      sparkle: source.sparkle,
      collisionScale: source.collisionScale,
    })
    // 学習者がテンプレート名（クローン元）に付けたプログラムをクローンでも実行
    // する（onPlay 起動 + onTouchCharacter 等のハンドラ登録。Roblox の Clone()
    // が Script ごと複製するのと同じ挙動。例: RJF09 のボールの乱数散布・接触で
    // かくす）。
    context.startObjectPrograms?.(id, name)
  }

  /** script.Parent.Position の 1 軸だけを設定する（setXTo/setYTo/setZTo）。 */
  private setPositionAxis(block: Blockly.Block): void {
    const state = this.selfState()
    if (!state) {
      return
    }
    const value = this.evaluator.readNumber(block, 'VAL')
    const position = {
      x: state.position.x,
      y: state.position.y,
      z: state.position.z,
    }
    if (block.type === 'movement_setXTo') {
      position.x = value
    } else if (block.type === 'movement_setYTo') {
      position.y = value
    } else {
      position.z = value
    }
    this.world.setPosition(this.self, position)
  }

  /** script.Parent.Size を設定/加算する（changeSizeBy/changeX/Y/ZSizeBy）。 */
  private changeSize(block: Blockly.Block): void {
    const state = this.selfState()
    if (!state) {
      return
    }
    const value = this.evaluator.readNumber(block, 'VAL')
    const size = { x: state.size.x, y: state.size.y, z: state.size.z }
    switch (block.type) {
      case 'looks_changeSizeBy':
        size.x += value
        size.y += value
        size.z += value
        break
      case 'looks_changeXSizeBy':
        size.x += value
        break
      case 'looks_changeYSizeBy':
        size.y += value
        break
      default:
        size.z += value
        break
    }
    this.world.setSize(this.self, size)
  }

  /** script.Parent.Size の 1 軸だけを設定する（setXSizeTo/setYSizeTo/setZSizeTo）。 */
  private setSizeAxis(block: Blockly.Block): void {
    const state = this.selfState()
    if (!state) {
      return
    }
    const value = this.evaluator.readNumber(block, 'VAL')
    const size = { x: state.size.x, y: state.size.y, z: state.size.z }
    if (block.type === 'looks_setXSizeTo') {
      size.x = value
    } else if (block.type === 'looks_setYSizeTo') {
      size.y = value
    } else {
      size.z = value
    }
    this.world.setSize(this.self, size)
  }

  /** self（script.Parent）の現在状態。destroy 後は undefined。 */
  private selfState(): ObjectRuntimeState | undefined {
    return this.world.getState().get(this.self)
  }

  /** 現在の完全姿勢 = Qy(rotationY) ∘ orientation（renderer の合成と同一）。 */
  private currentOrientation(state: ObjectRuntimeState): Quat {
    return quatMultiply(
      quatFromAxisAngle('y', state.rotationY),
      state.orientation,
    )
  }

  /**
   * 相対回転（CFrame *= CFrame.Angles(...)）を反映する。現在の完全姿勢へ単軸の
   * 回転を局所後掛け（current ⊗ delta）し、合成結果を setOrientation で確定する。
   */
  private applyRelativeRotation(
    block: Blockly.Block,
    axis: 'x' | 'y' | 'z',
    sign: number,
  ): void {
    const state = this.selfState()
    if (!state) {
      return
    }
    const delta = quatFromAxisAngle(
      axis,
      sign * degToRad(this.evaluator.readNumber(block, 'VAL')),
    )
    this.world.setOrientation(
      this.self,
      quatMultiply(this.currentOrientation(state), delta),
    )
  }

  private dispatchSendMessage(
    block: Blockly.Block,
    context: ExecutionContext,
  ): void {
    const message = block.getFieldValue('VAL')
    if (typeof message !== 'string' || message.length === 0) {
      console.warn(
        `[preview] events_sendMessage にメッセージ名がありません: ${block.id}`,
      )
      return
    }
    context.dispatchMessage(message)
  }

  private async runRepeat(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    const count = Math.floor(this.evaluator.readNumber(block, 'VAL'))
    const body = block.getInputTargetBlock('SUBSTACK')
    for (let i = 0; i < count; i += 1) {
      throwIfAborted(context.signal)
      await this.runStack(body, context)
      // body が空/disabled でも 1 イテレーションを 1 ステップとして数え、
      // 大回数 repeat でも stop() と他ハンドラに応答できるようにする。
      await this.maybeYield(context)
    }
  }

  private async runForever(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    const body = block.getInputTargetBlock('SUBSTACK')
    while (!context.signal.aborted) {
      await this.runStack(body, context)
      await this.luaWait(context)
    }
  }

  /**
   * 命令数ベースの協調 yield。待機なし長スタックでも他ハンドラと stop() に
   * 応答するための最小譲り（マイクロタスク 1 回ぶん）。
   */
  private yieldLoop(context: ExecutionContext): Promise<void> {
    this.statementsSinceYield = 0
    return delay(0, context.signal)
  }

  /**
   * Lua generator が forever/while の周回末尾に挿入する `wait()` 相当。
   * 実機の 30Hz スロットルに合わせて待機し、ループ速度を Roblox と揃える。
   */
  private luaWait(context: ExecutionContext): Promise<void> {
    this.statementsSinceYield = 0
    return this.wait(LUA_WAIT_SECONDS, context)
  }

  private async runIf(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    if (this.evaluator.readBoolean(block, 'CONDITION')) {
      await this.runStack(block.getInputTargetBlock('SUBSTACK'), context)
    }
  }

  private async runIfElse(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    const branch =
      this.evaluator.readBoolean(block, 'CONDITION') ? 'SUBSTACK' : 'SUBSTACK2'
    await this.runStack(block.getInputTargetBlock(branch), context)
  }

  private async runWhile(
    block: Blockly.Block,
    context: ExecutionContext,
  ): Promise<void> {
    const condition = block.getInputTargetBlock('CONDITION')
    // Lua generator は `while not (condition) do`（条件が false の間ループ）。
    // 条件入力が空、または disabled な値ブロックなら Lua の空入力フォールバック
    // `while false do` 相当で一度も実行しない（無限ループ防止）。
    if (!condition || !condition.isEnabled()) {
      return
    }
    const body = block.getInputTargetBlock('SUBSTACK')
    while (
      !context.signal.aborted &&
      !this.evaluator.evaluateBoolean(condition)
    ) {
      await this.runStack(body, context)
      await this.luaWait(context)
    }
  }

  private skipUnsupported(block: Blockly.Block): void {
    console.warn(
      `[preview] 未対応ブロックをスキップしました: ${block.type} (${block.id})`,
    )
  }
}
